"use client";

import React from "react";
import { Modal, Box, Typography, Button, Paper, alpha } from "@mui/material";
import { useAppTheme } from "../../context/theme.context";

const AlertModal = ({
  open,
  visible,
  onClose,
  onContinue,
  onConfirm,
  message,
  notice,
  title,
  continueText = "Continuar",
  confirmText,
  closeText = "Cancelar",
  severity = "warning", // 'warning', 'error', 'success', 'info'
  disableEscapeKeyDown = false,
  disableBackdropClick = false,
}) => {
  const { colors } = useAppTheme();
  const isVisible = open !== undefined ? Boolean(open) : Boolean(visible);
  const headerTitle = notice || title || "";
  const handleConfirm = onContinue || onConfirm || onClose;
  const actionText = confirmText || continueText;

  // Escape key handler
  React.useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === "Escape" && isVisible && onClose && !disableEscapeKeyDown) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isVisible, onClose, disableEscapeKeyDown]);

  const getConfirmButtonStyles = () => {
    if (severity === "error") {
      return {
        bgcolor: "#EF4444",
        color: "#FFFFFF",
        "&:hover": {
          bgcolor: "#DC2626",
        },
      };
    }
    return {
      background: colors.gradientPrimary || colors.primary,
      color: "#FFFFFF",
      "&:hover": {
        background: colors.gradientAccentHover || colors.primaryDark,
      },
    };
  };

  return (
    <Modal
      open={isVisible}
      onClose={onClose}
      closeAfterTransition
      disableEscapeKeyDown={disableEscapeKeyDown}
      disableAutoFocus
      BackdropProps={{
        timeout: 200,
        onClick: disableBackdropClick ? (e) => e.stopPropagation() : undefined,
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.4)",
          backdropFilter: "blur(3px)",
        },
      }}
    >
      <Box
        sx={{
          width: "100%",
          minHeight: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 2, sm: 3 },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 480,
            bgcolor: colors.surface || "background.paper",
            borderRadius: 3,
            boxShadow: "0 12px 32px rgba(0, 0, 0, 0.12)",
            border: `1px solid ${colors.sectionBorder || "rgba(0, 0, 0, 0.08)"}`,
            p: 0,
            overflow: "hidden",
          }}
        >
          {/* Header */}
          {headerTitle && (
            <Box
              sx={{
                p: { xs: 2.25, sm: 3 },
                bgcolor: alpha(colors.primary, 0.03),
                borderBottom: `1px solid ${colors.sectionBorder || "rgba(0, 0, 0, 0.06)"}`,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: severity === "error" ? colors.error : colors.primary,
                  fontSize: { xs: "1.15rem", sm: "1.25rem" },
                  lineHeight: 1.25,
                }}
              >
                {headerTitle}
              </Typography>
            </Box>
          )}

          {/* Content */}
          <Box sx={{ px: { xs: 2.25, sm: 3 }, py: { xs: 2.25, sm: 3 } }}>
            <Typography
              variant="body1"
              sx={{
                mb: 3,
                color: "text.primary",
                lineHeight: 1.55,
                fontSize: { xs: "0.95rem", sm: "1rem" },
              }}
            >
              {message}
            </Typography>

            {/* Actions */}
            <Box
              sx={{
                display: "flex",
                gap: 1.25,
                justifyContent: "flex-end",
                flexDirection: { xs: "column-reverse", sm: "row" },
              }}
            >
              {onClose && (
                <Button
                  variant="outlined"
                  onClick={onClose}
                  sx={{
                    borderRadius: 2,
                    borderColor: alpha(colors.textSecondary || "#6B7280", 0.3),
                    color: "text.primary",
                    minWidth: { xs: "100%", sm: 100 },
                    fontWeight: 600,
                    textTransform: "none",
                    "&:hover": {
                      borderColor: colors.primary,
                      backgroundColor: alpha(colors.primary, 0.04),
                    },
                  }}
                >
                  {closeText}
                </Button>
              )}

              <Button
                variant="contained"
                onClick={handleConfirm}
                sx={{
                  borderRadius: 2,
                  minWidth: { xs: "100%", sm: 110 },
                  fontWeight: 600,
                  textTransform: "none",
                  boxShadow: "none",
                  ...getConfirmButtonStyles(),
                }}
              >
                {actionText}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Modal>
  );
};

export default AlertModal;
